/**
 * Pure utility functions for grouping items by type/case/drinkware.
 * Extracted from product-creator.js mixin for reuse in both parent components
 * and the NylonColorStylePanel child component.
 */

import { productTypes } from "@/lib/product-creator.js";

// A Tee's group by blank fit; any other fit groups as plain "Tee".
const TEE_GROUPS = new Map([
  ["Men", "Unisex Tee"],
  ["Unisex", "Unisex Tee"],
  ["Women", "Women’s Tee"],
  ["Youth", "Youth Tee"],
  ["Toddler", "Toddler Tee"],
]);

/**
 * Get the grouping key for an item based on product type.
 * @param {string} productType - The product type (clothing, phonecase, hat, etc.)
 * @param {object} item - The item to get the group key for
 * @returns {string|null} The group key
 */
export function getItemGroupKey(productType, item) {
  switch (productType) {
    case productTypes.APPAREL:
      return item.blank.type === "Tee" ? TEE_GROUPS.get(item.blank.fit) || item.blank.type : item.blank.type;
    case productTypes.PHONECASE:
      return item.case_name;
    case productTypes.HAT:
    case productTypes.BEANIE:
    case productTypes.TOTE:
      return item.style;
    case productTypes.DRINKWARE:
    case productTypes.STICKER:
      return item.name;
    default:
      return null;
  }
}

// Items in first-seen order of their getItemGroupKey(), as [{ name, items }].
function groupByKey(items, productType) {
  const groups = [];
  for (const item of items) {
    const name = getItemGroupKey(productType, item);
    let group = groups.find((g) => g.name === name);
    if (!group) groups.push((group = { name, items: [] }));
    group.items.push(item);
  }
  return groups;
}

/**
 * Group items by phone case type.
 * @param {array} items - The items to group
 * @param {string} productType - The product type
 * @returns {array} Array of case groups with name and items
 */
export function itemsGroupedByCase(items, productType) {
  return groupByKey(items, productType);
}

/**
 * Group items by apparel type (Unisex Tee, Women's Tee, etc.) and category.
 * @param {array} items - The items to group
 * @param {boolean} flatten - If true, returns flat array of items instead of grouped structure
 * @returns {array} Array of type groups with categories and items
 */
export function itemsGroupedByType(items, flatten = false) {
  items = items.filter((item) => item.canBeIncluded);

  const types = [];

  items.forEach((item) => {
    const groupKey = item.group;
    const subGroupKey = item.subGroup;

    let matchingType = types.find((type) => type.name === groupKey);

    if (!matchingType) {
      matchingType = {
        name: groupKey,
        categories: [],
      };

      types.push(matchingType);
    }

    let matchingCategory = matchingType.categories.find((category) => category.name === subGroupKey);

    if (!matchingCategory) {
      matchingCategory = {
        name: subGroupKey,
        items: [],
      };

      matchingType.categories.push(matchingCategory);
    }

    matchingCategory.items.push(item);
  });

  if (flatten) {
    return types.flatMap((t) => t.categories.flatMap((c) => c.items));
  }

  return types;
}

/**
 * Group items by drinkware type.
 * @param {array} items - The items to group
 * @param {string} productType - The product type
 * @returns {array} Array of drinkware type groups with name and items
 */
export function itemsGroupedByDrinkwareType(items, productType) {
  return groupByKey(items, productType);
}
